import React, { useEffect, useRef, useState } from "react";

/**
 * Port of "Property Direct Hero.dc.html" (Claude Design), adapted for a full page.
 *
 * Two exports:
 *   • AnimatedHeroBackground (default) — a position:fixed full-viewport backdrop:
 *       soft blue-white gradient + brand blobs + a faint Three.js blueprint city
 *       (line-art towers, network lines, flowing particles, scan sweep, dust),
 *       mouse parallax + gentle scroll drift. Renders once, behind the entire page
 *       so every section sits on the same living backdrop instead of going blank.
 *   • HeroHud (named) — the hero-only overlay: four glass HUD cards (≥1180px) and
 *       the readability glow. Render inside the hero <section>.
 *
 * three.js r128 loads from CDN at runtime (not bundled). Honors prefers-reduced-motion
 * (one static frame) and pauses on tab hide.
 */

const KEYFRAMES = `
  @keyframes pd-drift-a { 0%,100% { transform: translate3d(0,0,0); } 50% { transform: translate3d(0,-13px,0); } }
  @keyframes pd-drift-b { 0%,100% { transform: translate3d(0,0,0); } 50% { transform: translate3d(0,11px,0); } }
  @keyframes pd-drift-c { 0%,100% { transform: translate3d(0,0,0); } 50% { transform: translate3d(7px,-9px,0); } }
  @keyframes pd-pulse { 0%,100% { opacity: .35; } 50% { opacity: 1; } }
  @keyframes pd-badge { 0%,100% { box-shadow: 0 0 0 0 rgba(59,110,246,0.30); } 50% { box-shadow: 0 0 0 6px rgba(59,110,246,0); } }
  @keyframes pd-bar { 0%,100% { transform: scaleY(0.5); } 50% { transform: scaleY(1); } }
`;

const FONTS_HREF =
  "https://fonts.googleapis.com/css2?family=Sora:wght@500;600;700;800&family=Manrope:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap";
const THREE_SRC = "https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js";

function injectOnce(id: string, make: () => HTMLElement) {
  if (document.getElementById(id)) return;
  document.head.appendChild(make());
}

function ensureAssets() {
  injectOnce("ahb-fonts", () => {
    const l = document.createElement("link");
    l.id = "ahb-fonts"; l.rel = "stylesheet"; l.href = FONTS_HREF;
    return l;
  });
  injectOnce("ahb-kf", () => {
    const st = document.createElement("style");
    st.id = "ahb-kf"; st.textContent = KEYFRAMES;
    return st;
  });
}

function loadThree(): Promise<any> {
  return new Promise(resolve => {
    const w = window as any;
    if (w.THREE) return resolve(w.THREE);
    const existing = document.getElementById("three-r128") as HTMLScriptElement | null;
    if (existing) { existing.addEventListener("load", () => resolve(w.THREE)); return; }
    const s = document.createElement("script");
    s.id = "three-r128"; s.src = THREE_SRC; s.async = true;
    s.onload = () => resolve(w.THREE);
    document.head.appendChild(s);
  });
}

/* ───────────────────────── Fixed full-page backdrop ───────────────────────── */

const AnimatedHeroBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    ensureAssets();

    let alive = true;
    let raf = 0;
    const reduced =
      window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const mouse = { x: 0, y: 0 };
    const target = { x: 0, y: 0 };
    let scrollY = window.scrollY || 0;

    let renderer: any, scene: any, camera: any, city: any, clock: any, baseCam: any;
    let buildings: any[] = [];
    let flows: any[] = [];
    let flowGeo: any, dustGeo: any, scan: any;

    const onResize = () => {
      if (!renderer || !canvasRef.current) return;
      const c = canvasRef.current;
      const w = c.clientWidth, h = c.clientHeight;
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    const onMove = (e: MouseEvent) => {
      target.x = (e.clientX / window.innerWidth) * 2 - 1;
      target.y = (e.clientY / window.innerHeight) * 2 - 1;
    };
    const onScroll = () => { scrollY = window.scrollY || 0; };
    const onVisibility = () => {
      if (reduced) return;
      if (document.hidden) { if (raf) cancelAnimationFrame(raf); raf = 0; }
      else if (!raf && clock) raf = requestAnimationFrame(animate);
    };

    function init(THREE: any) {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const W = canvas.clientWidth || 1, H = canvas.clientHeight || 1;
      const BG = 0xf3f7ff;

      renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      renderer.setSize(W, H, false);
      renderer.setClearColor(0x000000, 0);

      scene = new THREE.Scene();
      scene.fog = new THREE.Fog(BG, 58, 168);

      camera = new THREE.PerspectiveCamera(52, W / H, 0.1, 400);
      baseCam = new THREE.Vector3(0, 21, 60);
      camera.position.copy(baseCam);
      camera.lookAt(0, 9, -4);

      city = new THREE.Group();
      city.position.y = -1.5;
      scene.add(city);

      const grid = new THREE.GridHelper(220, 44, 0x9cb8ee, 0xc6d7f4);
      grid.material.transparent = true;
      grid.material.opacity = 0.4;
      city.add(grid);

      const edgeColors = [0x3b6ef6, 0x4f7cff, 0x5f8bff, 0x2f5fe0];
      const faceMat = new THREE.MeshBasicMaterial({
        color: 0x9fc0ff, transparent: true, opacity: 0.06, depthWrite: false, side: THREE.DoubleSide,
      });

      const specs: any[] = [];
      const cols = 7, rows = 7, cell = 13;
      for (let i = 0; i < cols; i++) {
        for (let j = 0; j < rows; j++) {
          if (Math.random() < 0.2) continue;
          const x = (i - (cols - 1) / 2) * cell + (Math.random() - 0.5) * 3.5;
          const z = (j - (rows - 1) / 2) * cell + (Math.random() - 0.5) * 3.5;
          const w = 4 + Math.random() * 4.5, d = 4 + Math.random() * 4.5, h = 6 + Math.random() * 32;
          specs.push({ x, z, w, d, h });
        }
      }

      buildings = [];
      const tops: any[] = [];
      specs.forEach((s, idx) => {
        const g = new THREE.Group();
        const geo = new THREE.BoxGeometry(s.w, s.h, s.d);
        g.add(new THREE.Mesh(geo, faceMat));
        g.add(new THREE.LineSegments(
          new THREE.EdgesGeometry(geo),
          new THREE.LineBasicMaterial({ color: edgeColors[idx % edgeColors.length], transparent: true, opacity: 0.28 })
        ));
        g.position.set(s.x, s.h / 2, s.z);
        city.add(g);
        buildings.push({ g, baseY: s.h / 2, phase: Math.random() * Math.PI * 2, speed: 0.35 + Math.random() * 0.35 });
        tops.push(new THREE.Vector3(s.x, s.h, s.z));
      });

      const linePts: any[] = [];
      const segs: any[] = [];
      for (let a = 0; a < tops.length; a++) {
        let made = 0;
        for (let b = a + 1; b < tops.length && made < 2; b++) {
          if (tops[a].distanceTo(tops[b]) < cell * 1.7 && Math.random() < 0.6) {
            linePts.push(tops[a].clone(), tops[b].clone());
            segs.push({ a: tops[a], b: tops[b], arc: 3 + Math.random() * 6 });
            made++;
          }
        }
      }
      const lineGeo = new THREE.BufferGeometry().setFromPoints(linePts);
      city.add(new THREE.LineSegments(lineGeo, new THREE.LineBasicMaterial({ color: 0x4f7cff, transparent: true, opacity: 0.16 })));

      const fCount = Math.min(segs.length, 90);
      flows = [];
      const fPos = new Float32Array(fCount * 3);
      for (let i = 0; i < fCount; i++) {
        const seg = segs[i % segs.length];
        if (!seg) break;
        flows.push({ seg, t: Math.random(), speed: 0.0024 + Math.random() * 0.0042 });
      }
      flowGeo = new THREE.BufferGeometry();
      flowGeo.setAttribute("position", new THREE.BufferAttribute(fPos, 3));
      city.add(new THREE.Points(flowGeo, new THREE.PointsMaterial({ color: 0x2f5fe0, size: 0.85, transparent: true, opacity: 0.7, depthWrite: false })));

      scan = new THREE.Mesh(
        new THREE.PlaneGeometry(190, 190),
        new THREE.MeshBasicMaterial({ color: 0x4f7cff, transparent: true, opacity: 0.05, depthWrite: false, side: THREE.DoubleSide })
      );
      scan.rotation.x = -Math.PI / 2;
      city.add(scan);

      const dCount = 240;
      const dPos = new Float32Array(dCount * 3);
      for (let i = 0; i < dCount; i++) {
        dPos[i * 3] = (Math.random() - 0.5) * 200;
        dPos[i * 3 + 1] = Math.random() * 70;
        dPos[i * 3 + 2] = (Math.random() - 0.5) * 160;
      }
      dustGeo = new THREE.BufferGeometry();
      dustGeo.setAttribute("position", new THREE.BufferAttribute(dPos, 3));
      scene.add(new THREE.Points(dustGeo, new THREE.PointsMaterial({ color: 0x6f97ff, size: 0.5, transparent: true, opacity: 0.3, depthWrite: false })));

      clock = new THREE.Clock();
      if (reduced) { renderer.render(scene, camera); return; }
      raf = requestAnimationFrame(animate);
    }

    function animate() {
      if (!alive) return;
      raf = requestAnimationFrame(animate);
      const t = clock.getElapsedTime();
      const sy = Math.min(scrollY, 3000);

      for (const b of buildings) b.g.position.y = b.baseY + Math.sin(t * b.speed + b.phase) * 0.35;
      city.rotation.y = Math.sin(t * 0.04) * 0.05 + sy * 0.00004;

      const fp = flowGeo.attributes.position.array;
      for (let i = 0; i < flows.length; i++) {
        const f = flows[i];
        f.t += f.speed; if (f.t > 1) f.t -= 1;
        const tt = f.t;
        fp[i * 3] = f.seg.a.x + (f.seg.b.x - f.seg.a.x) * tt;
        fp[i * 3 + 1] = f.seg.a.y + (f.seg.b.y - f.seg.a.y) * tt + Math.sin(Math.PI * tt) * f.seg.arc;
        fp[i * 3 + 2] = f.seg.a.z + (f.seg.b.z - f.seg.a.z) * tt;
      }
      flowGeo.attributes.position.needsUpdate = true;

      scan.position.y = (t * 4.5) % 56;
      scan.material.opacity = 0.03 + Math.abs(Math.sin(t * 1.4)) * 0.045;

      const dp = dustGeo.attributes.position.array;
      for (let i = 0; i < dp.length; i += 3) { dp[i + 1] += 0.012; if (dp[i + 1] > 72) dp[i + 1] = 0; }
      dustGeo.attributes.position.needsUpdate = true;

      mouse.x += (target.x - mouse.x) * 0.04;
      mouse.y += (target.y - mouse.y) * 0.04;
      camera.position.x = baseCam.x + mouse.x * 6;
      camera.position.y = baseCam.y + Math.sin(t * 0.3) * 0.7 - mouse.y * 3 + sy * 0.0025;
      camera.position.z = baseCam.z + Math.cos(t * 0.22) * 1.2;
      camera.lookAt(0, 9, -4);

      renderer.render(scene, camera);
    }

    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, { passive: true });
    if (!reduced) window.addEventListener("mousemove", onMove, { passive: true });
    document.addEventListener("visibilitychange", onVisibility);

    loadThree().then(THREE => { if (alive && THREE) init(THREE); });

    return () => {
      alive = false;
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("mousemove", onMove);
      document.removeEventListener("visibilitychange", onVisibility);
      if (renderer) { try { renderer.dispose(); } catch (e) { /* noop */ } }
    };
  }, []);

  return (
    <div
      aria-hidden="true"
      style={{
        position: "fixed", inset: 0, zIndex: 0, overflow: "hidden", pointerEvents: "none",
        background: "radial-gradient(120% 80% at 50% 0%, #eaf1ff 0%, #f1f6ff 38%, #f6f9ff 100%)",
      }}
    >
      {/* soft brand blobs */}
      <div style={{ position: "absolute", top: "-12%", left: "-8%", width: "46vw", height: "46vw", borderRadius: "50%", background: "radial-gradient(circle, rgba(110,151,255,0.20) 0%, rgba(110,151,255,0) 65%)" }} />
      <div style={{ position: "absolute", bottom: "-18%", right: "-10%", width: "50vw", height: "50vw", borderRadius: "50%", background: "radial-gradient(circle, rgba(79,124,255,0.16) 0%, rgba(79,124,255,0) 64%)" }} />
      {/* 3D blueprint city */}
      <canvas ref={canvasRef} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", display: "block" }} />
    </div>
  );
};

export default AnimatedHeroBackground;

/* ───────────────────────────── Hero-only overlay ──────────────────────────── */

const glass: React.CSSProperties = {
  position: "absolute", borderRadius: 16, background: "rgba(255,255,255,0.72)",
  border: "1px solid rgba(59,110,246,0.12)", backdropFilter: "blur(10px)",
  WebkitBackdropFilter: "blur(10px)", boxShadow: "0 22px 50px -26px rgba(34,67,140,0.45)",
};
const monoLabel: React.CSSProperties = {
  fontFamily: "'JetBrains Mono', monospace", color: "#3b6ef6", textTransform: "uppercase",
};

export const HeroHud: React.FC = () => {
  const [showHud, setShowHud] = useState(
    typeof window !== "undefined" ? window.innerWidth >= 1180 : false
  );

  useEffect(() => {
    ensureAssets();
    const onResize = () => setShowHud(window.innerWidth >= 1180);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return (
    <>
      {showHud && (
        <div aria-hidden="true" style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 1 }}>

          {/* maintenance collected */}
          <div style={{ ...glass, left: "3%", top: "30%", width: "clamp(160px,13vw,204px)", padding: "15px 16px", opacity: 0.95, animation: "pd-drift-a 11s ease-in-out infinite" }}>
            <div style={{ ...monoLabel, fontSize: 10, letterSpacing: "0.12em" }}>Maintenance collected</div>
            <div style={{ fontFamily: "'Sora', sans-serif", fontWeight: 700, fontSize: 25, color: "#0e1730", marginTop: 4, lineHeight: 1 }}>
              ₹4.28<span style={{ fontSize: 14, color: "#5a6a85" }}>L</span>
            </div>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 5, height: 30, marginTop: 12 }}>
              {[0, 0.4, 0.8, 1.2, 1.6, 2].map((d, i) => (
                <div key={i} style={{ width: 7, height: "100%", borderRadius: 3, background: "linear-gradient(180deg,#6f97ff,#3b6ef6)", transformOrigin: "bottom", animation: `pd-bar 3.2s ease-in-out infinite ${d}s` }} />
              ))}
            </div>
          </div>

          {/* smart bill */}
          <div style={{ ...glass, left: "3.5%", top: "62%", width: "clamp(160px,13vw,196px)", padding: "14px 15px", opacity: 0.92, animation: "pd-drift-c 13s ease-in-out infinite" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <div style={{ width: 8, height: 8, borderRadius: 2, background: "#3b6ef6" }} />
              <div style={{ ...monoLabel, fontSize: 9.5, letterSpacing: "0.1em" }}>Smart bill · Flat A-204</div>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11.5, color: "#5a6a85", marginBottom: 6 }}><span>Maintenance</span><span style={{ color: "#0e1730", fontWeight: 600 }}>₹3,200</span></div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11.5, color: "#5a6a85", marginBottom: 6 }}><span>Water · units</span><span style={{ color: "#0e1730", fontWeight: 600 }}>₹640</span></div>
            <div style={{ height: 1, background: "rgba(59,110,246,0.12)", margin: "8px 0" }} />
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#0e1730", fontWeight: 700 }}><span>Total due</span><span style={{ color: "#3b6ef6" }}>₹3,840</span></div>
          </div>

          {/* dues cleared ring */}
          <div style={{ ...glass, right: "3.5%", top: "30%", width: "clamp(126px,10vw,154px)", padding: 16, opacity: 0.95, display: "flex", flexDirection: "column", alignItems: "center", animation: "pd-drift-b 12s ease-in-out infinite" }}>
            <div style={{ width: 76, height: 76, borderRadius: "50%", background: "conic-gradient(#3b6ef6 0% 92%, rgba(59,110,246,0.14) 92% 100%)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <div style={{ width: 56, height: 56, borderRadius: "50%", background: "#ffffff", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Sora', sans-serif", fontWeight: 700, fontSize: 17, color: "#0e1730" }}>92%</div>
            </div>
            <div style={{ ...monoLabel, fontSize: 9.5, letterSpacing: "0.1em", marginTop: 12 }}>Dues cleared</div>
          </div>

          {/* AI scan node */}
          <div style={{ ...glass, right: "3.5%", top: "60%", width: "clamp(160px,12vw,190px)", padding: "14px 15px", opacity: 0.92, animation: "pd-drift-c 12s ease-in-out infinite 0.9s" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 9, height: 9, borderRadius: "50%", background: "#3b6ef6", boxShadow: "0 0 0 4px rgba(59,110,246,0.14)", animation: "pd-pulse 2.4s ease-in-out infinite" }} />
              <div style={{ ...monoLabel, fontSize: 10, letterSpacing: "0.11em" }}>Smart Scan · AI</div>
            </div>
            <div style={{ fontFamily: "'Manrope', sans-serif", fontSize: 11.5, color: "#5a6a85", marginTop: 9, lineHeight: 1.45 }}>Reading meters &amp; invoices across 3 towers…</div>
          </div>

        </div>
      )}

      {/* readability glow behind hero text */}
      <div aria-hidden="true" style={{ position: "absolute", inset: 0, zIndex: 2, pointerEvents: "none", background: "radial-gradient(46% 42% at 50% 42%, rgba(246,249,255,0.92) 0%, rgba(246,249,255,0.55) 48%, rgba(246,249,255,0) 74%)" }} />
    </>
  );
};
